import { useRef } from "react";
import {
    motion,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { PhoneMockup } from "@/components/public/mockups/PhoneMockup";
import { DesktopMockup } from "@/components/public/mockups/DesktopMockup";

export const Hero3DScene = () => {
    const ref = useRef<HTMLDivElement>(null);
    const prefersReducedMotion = useReducedMotion();

    // Mouse tracking across the scene
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const mxSpring = useSpring(mx, { stiffness: 80, damping: 18 });
    const mySpring = useSpring(my, { stiffness: 80, damping: 18 });

    // Scroll-linked progress
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    // Desktop: tilts toward cursor + subtle scroll drift
    const desktopRotateY = useTransform(mxSpring, [-0.5, 0.5], [-6, 6]);
    const desktopRotateX = useTransform(mySpring, [-0.5, 0.5], [4, -4]);
    const desktopScrollY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    // Phone: opposite tilt for depth feel + stronger scroll parallax
    const phoneRotateY = useTransform(mxSpring, [-0.5, 0.5], [10, -10]);
    const phoneRotateX = useTransform(mySpring, [-0.5, 0.5], [-6, 6]);
    const phoneScrollY = useTransform(scrollYProgress, [0, 1], [0, -100]);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!ref.current || prefersReducedMotion) return;
        const rect = ref.current.getBoundingClientRect();
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const handleMouseLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative h-[480px] w-full lg:h-[560px]"
            style={{ perspective: "1400px" }}
        >
            {/* Ambient glow behind the scene */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[120px]" />
                <div className="absolute bottom-4 left-4 h-[240px] w-[240px] rounded-full bg-secondary/10 blur-[100px]" />
            </div>

            {/* Desktop — centered, tilted */}
            <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 60, rotateX: -18 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                style={{
                    y: desktopScrollY,
                    rotateY: prefersReducedMotion ? 0 : desktopRotateY,
                    rotateX: prefersReducedMotion ? 0 : desktopRotateX,
                    transformStyle: "preserve-3d",
                }}
                className="absolute left-1/2 top-[42%] w-[380px] -translate-x-1/2 -translate-y-1/2 lg:w-[420px]"
            >
                <div className="animate-float" style={{ transformStyle: "preserve-3d" }}>
                    <DesktopMockup imageSrc="/mockups/desktop-dashboard.png" />
                </div>
            </motion.div>

            {/* Phone — outer edge (visual-left in RTL) so it doesn't cover text column */}
            <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 80, rotateY: 30 }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
                style={{
                    y: phoneScrollY,
                    rotateY: prefersReducedMotion ? 0 : phoneRotateY,
                    rotateX: prefersReducedMotion ? 0 : phoneRotateX,
                    transformStyle: "preserve-3d",
                }}
                className="absolute bottom-4 left-0 lg:left-4"
            >
                <div
                    style={{
                        transform: "translateZ(80px)",
                        transformStyle: "preserve-3d",
                        filter: "drop-shadow(0 30px 30px rgba(0,0,0,0.5))",
                    }}
                >
                    <div
                        className="animate-float"
                        style={{ animationDelay: "1.2s", transformStyle: "preserve-3d" }}
                    >
                        <PhoneMockup size="md" imageSrc="/mockups/kasioon-phone.png" />
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Hero3DScene;
